import React, { useState, useEffect } from "react";
import {
    motion,
    useScroll,
    useMotionValueEvent,
    AnimatePresence,
} from "motion/react";
import {
    Menu, X, ChevronDown, ChevronLeft, ChevronRight, UserPlus, Building2,
} from "lucide-react";
import wizLogo from "@images/logo.webp";
import { usePage } from "@inertiajs/react";
import ServicesMegaMenu, { SERVICE_CATEGORIES, MEGA_MENU_ICONS } from "./ServicesMegaMenu";
import SeoMegaMenu, { SEO_HUB_CATEGORIES } from "./SeoMegaMenu";
import useIsMobile from "@/hooks/useIsMobile";

// Mobile-only promo ticker, shown above the nav — see the ticker markup
// further down for how these loop.
const TICKER_MESSAGES = [
    "استشارة مجانية لمشروعك",
    "حملات إعلانية على جوجل وميتا وسناب شات وتيك توك",
    "نتائج قابلة للقياس",
    "فريق متخصص ودعم مستمر",
];

const navLinks = [
    { name: "الرئيسية", href: "/" },
    { name: "خدماتنا", dropdown: true, key: "services" },
    // Deliberately "خدمات SEO", not "شركة سيو" — the single real SEO
    // service page is one link inside this menu, not the menu's name, so
    // the same structure scales to more sectors/cities later without ever
    // needing a rename. See SeoMegaMenu.jsx for the full rationale.
    { name: "خدمات SEO", dropdown: true, key: "seo" },
    { name: "القطاعات", dropdown: true, key: "industries" },
    // { name: "الخدمات المجانية", dropdown: true, key: "tools" },
    { name: "معرض الاعمال", href: "/portfolio" },
    { name: "المدونة", href: "/blogs" },
    { name: "عن الشركة", dropdown: true, key: "about" },
];

const aboutLinks = [
    { name: "من نحن", href: "/about" },
    { name: "البروفايل", href: "/profile" },
];

// Desktop "القطاعات" mega-menu + mobile drawer accordion — slugs mirror
// IndustryCatalog.php exactly, so every link here resolves to a real page.
const industriesLinks = [
    { name: "تسويق المتاجر الإلكترونية", href: "/ecommerce-marketing" },
    { name: "تسويق المطاعم والكافيهات", href: "/restaurant-marketing" },
    { name: "تسويق العيادات والمراكز الطبية", href: "/medical-marketing" },
    { name: "تسويق الشركات والمؤسسات", href: "/corporate-marketing" },
    { name: "تسويق العقارات والمقاولات", href: "/real-estate-marketing" },
    { name: "تسويق التعليم والأكاديميات", href: "/education-marketing" },
    { name: "تسويق شركات التقنية", href: "/technology-marketing" },
    { name: "تسويق مكاتب المحاماة", href: "/law-firm-marketing" },
    { name: "تسويق شركات السفر والسياحة", href: "/travel-marketing" },
    { name: "تسويق التجميل والعيادات", href: "/beauty-marketing" },
    { name: "تسويق السيارات والمعارض", href: "/automotive-marketing" },
    { name: "تسويق المشاريع الناشئة", href: "/startup-marketing" },
];

const toolsLinks = [
    { name: "فحص سرعة الموقع", href: "/tools/website-speed-test" },
    // { name: "تحليل السيو", href: "/tools/seo-analysis" },
    // { name: "اكتشف افضل الكلمات المفتاحيه", href: "/tools/keywords-explorer" },
];

const dropdownGroups = {
    about: aboutLinks,
    tools: toolsLinks,
    industries: industriesLinks,
};

const Header = () => {
    const { url } = usePage();
    const isActive = (linkHref) => {
        if (linkHref === "/") {
            return url === "/";
        }
        return url.startsWith(linkHref);
    };
    const [hidden, setHidden] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    // Mobile keeps the header on screen at all times — `hidden` still
    // tracks scroll direction the same way it always has, but on mobile it
    // now drives a compact/shrunk bar instead of the full translate-away
    // used on desktop (see the motion.header animate prop and the mobile
    // row's className below).
    const isMobile = useIsMobile();
    const [menuOpen, setMenuOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null); // drawer accordion: null | 'about' | 'services' | 'tools'

    // Desktop dropdown: which one (if any) is open — null | 'about' |
    // 'services' | 'tools' | 'industries'. A single keyed state instead of
    // one boolean per dropdown, so adding another mega-menu later is a
    // one-line addition instead of touching four call sites.
    const [openDesktopDropdown, setOpenDesktopDropdown] = useState(null);

    const { scrollY } = useScroll();

    useMotionValueEvent(scrollY, "change", (latest) => {
        const previous = scrollY.getPrevious();

        // Hide header when scrolling down past 100px
        if (latest > previous && latest > 100) {
            setHidden(true);
        }
        // Show header when scrolling up
        else if (latest < previous) {
            setHidden(false);
        }

        // Always show header at the very top
        if (latest < 50) {
            setHidden(false);
        }

        // Slightly deeper shadow + blurred backdrop once scrolled away from
        // the top — a subtle "lifted" state, not a layout change, so it
        // can't introduce CLS.
        setScrolled(latest > 20);
    });

    // Close everything on route change
    useEffect(() => {
        setMenuOpen(false);
        setOpenDropdown(null);
        setOpenDesktopDropdown(null);
    }, [url]);

    // Close mobile drawer / desktop dropdowns when the header auto-hides on scroll
    useEffect(() => {
        if (hidden) {
            setMenuOpen(false);
            setOpenDesktopDropdown(null);
        }
    }, [hidden]);

    // Lock body scroll while the mobile drawer is open
    useEffect(() => {
        if (!menuOpen) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = original;
        };
    }, [menuOpen]);

    // Close desktop dropdown on outside click
    useEffect(() => {
        if (!openDesktopDropdown) return;

        const handleClick = (e) => {
            const dropdownClass = `desktop-${openDesktopDropdown}-dropdown`;
            if (
                !e.target.closest(`.${dropdownClass}`) &&
                !e.target.closest(`.${dropdownClass}-btn`)
            ) {
                setOpenDesktopDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [openDesktopDropdown]);

    // Keyboard support for the open desktop dropdown: Escape closes and
    // returns focus to its trigger button; Up/Down cycles through the
    // panel's menuitems (wrapping at both ends).
    useEffect(() => {
        if (!openDesktopDropdown) return;

        const handleKeyDown = (e) => {
            const dropdownClass = `desktop-${openDesktopDropdown}-dropdown`;
            const panel = document.querySelector(`.${dropdownClass} [role="menu"]`);

            if (e.key === "Escape") {
                e.preventDefault();
                setOpenDesktopDropdown(null);
                document.querySelector(`.${dropdownClass}-btn`)?.focus();
                return;
            }

            if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
            if (!panel) return;
            e.preventDefault();

            const items = Array.from(panel.querySelectorAll('[role="menuitem"]'));
            if (items.length === 0) return;

            const currentIndex = items.indexOf(document.activeElement);
            const nextIndex =
                e.key === "ArrowDown"
                    ? (currentIndex + 1) % items.length
                    : (currentIndex - 1 + items.length) % items.length;
            items[nextIndex].focus();
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [openDesktopDropdown]);

    const closeDrawer = () => {
        setMenuOpen(false);
        setOpenDropdown(null);
    };

    return (
        <motion.header
            className="site-header fixed top-0 left-0 right-0 z-[100] px-4 pt-4"
            variants={{
                visible: { y: 0, opacity: 1 },
                hidden: { y: "-130%", opacity: 0 },
            }}
            initial="visible"
            animate={!isMobile && hidden ? "hidden" : "visible"}
            transition={{
                duration: 0.5,
                ease: [0.25, 0.1, 0.25, 1],
                opacity: { duration: 0.3 },
            }}
        >
            <div className="max-w-[1320px] mx-auto">
                {/* Slim animated promo ticker, mobile only — sits above the
                    floating nav pill. Collapses to 0 height (not removed —
                    animating height/margin directly is normally a CLS risk,
                    but this element is `fixed`-context, outside document
                    flow relative to the page content, so it can't shift
                    anything below it) once the user scrolls past the same
                    threshold that used to fully hide the header, so the
                    compact bar reads as genuinely smaller instead of just
                    the nav row shrinking while a full-height ticker stays
                    put above it. */}
                <div
                    className={`lg:hidden relative overflow-hidden rounded-2xl transition-all duration-300 ease-out ${
                        isMobile && hidden ? "h-0 mb-0 opacity-0" : "h-11 mb-2 opacity-100"
                    }`}
                >
                    {/* Seamless RTL loop: two identical copies, each carrying
                        its own trailing gap (paddingInlineEnd), slid by
                        exactly one copy (+50%) toward the right — the
                        natural reading flow for Arabic. Sliding left (the
                        old -50%) exposed an empty bar under dir="rtl". */}
                    <div
                        className="flex h-full items-center whitespace-nowrap px-9"
                        style={{ background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)" }}
                    >
                        <div className="wiz-ticker-track flex shrink-0 items-center">
                            {[0, 1].map((copy) => (
                                <div key={copy} className="flex shrink-0 items-center" aria-hidden={copy === 1 ? "true" : undefined}>
                                    {TICKER_MESSAGES.map((msg) => (
                                        <span key={msg} className="flex shrink-0 items-center" style={{ gap: 14, paddingInlineEnd: 14 }}>
                                            <span className="text-white font-bold" style={{ fontSize: 13, letterSpacing: 0.1 }}>{msg}</span>
                                            <span className="h-1 w-1 rounded-full bg-white/60" aria-hidden="true" />
                                        </span>
                                    ))}
                                </div>
                            ))}
                        </div>
                        <style>{`
                            .wiz-ticker-track {
                                animation: wizTickerRtl 32s linear infinite;
                                will-change: transform;
                            }
                            @keyframes wizTickerRtl {
                                from { transform: translate3d(0, 0, 0); }
                                to { transform: translate3d(50%, 0, 0); }
                            }
                            @media (prefers-reduced-motion: reduce) {
                                .wiz-ticker-track { animation: none; }
                            }
                        `}</style>
                    </div>
                    {/* Decorative only — the ticker auto-scrolls continuously,
                        these just echo the reference's carousel-style ends. */}
                    <span className="pointer-events-none absolute inset-y-0 right-1.5 flex items-center text-white/70">
                        <ChevronRight className="h-4 w-4" />
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 left-1.5 flex items-center text-white/70">
                        <ChevronLeft className="h-4 w-4" />
                    </span>
                </div>

                {/* Deliberately plain opaque `bg-white` — no backdrop-filter
                    here. This wrapper contains the desktop dropdown/mega-menu
                    panels (position:fixed, meant to escape to the page's root
                    stacking context). Any filter/backdrop-filter/transform on
                    this ancestor creates a new stacking context that traps
                    those fixed-position descendants inside it instead, which
                    then renders them BEHIND the sibling dropdown backdrop's
                    blur layer — the exact bug this comment is guarding
                    against (don't reintroduce it for a scroll-polish effect). */}
                <div
                    className={`bg-white rounded-[24px] border border-[#E5E7EB] transition-shadow duration-300 ${
                        scrolled ? "shadow-header-scrolled" : "shadow-header"
                    }`}
                >
                    {/* Desktop / tablet-landscape row (lg and up) */}
                    <div className="header-desktop-row hidden lg:flex items-center gap-5 justify-between px-6 h-[76px]">
                        <a href="/" className="shrink-0">
                            <img
                                src={wizLogo}
                                alt="Wiz Freelance"
                                className="w-14 h-auto object-contain"
                            />
                        </a>

                        <nav className="flex items-center justify-center gap-7 py-2">
                            {navLinks.map((link) => {
                                if (link.dropdown) {
                                    const isServices = link.key === "services";
                                    const isIndustries = link.key === "industries";
                                    const isSeo = link.key === "seo";

                                    const isOpen = openDesktopDropdown === link.key;
                                    const setOpen = (next) =>
                                        setOpenDesktopDropdown(next ? link.key : null);

                                    const dropdownLinks =
                                        dropdownGroups[link.key];

                                    const dropdownClass = `desktop-${link.key}-dropdown`;
                                    const btnClass = `desktop-${link.key}-dropdown-btn`;

                                    return (
                                        <div
                                            key={link.name}
                                            className={`relative ${dropdownClass}`}
                                        >
                                            <button
                                                className={`${btnClass} shrink-0 whitespace-nowrap rounded-full px-3.5 py-2 font-medium text-[14px] transition-colors flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 ${
                                                    isOpen
                                                        ? "bg-primary-50 text-primary-600"
                                                        : "text-gray-600 hover:text-primary-500"
                                                }`}
                                                tabIndex={0}
                                                onClick={() => setOpen(!isOpen)}
                                                aria-expanded={isOpen}
                                                aria-label={link.name}
                                            >
                                                {link.name}
                                                <ChevronDown
                                                    className={`w-3.5 h-3.5 transition-transform ${
                                                        isOpen
                                                            ? "rotate-180"
                                                            : ""
                                                    }`}
                                                    aria-hidden="true"
                                                />
                                            </button>
                                            {isOpen && isServices && (
                                                <ServicesMegaMenu onClose={() => setOpen(false)} />
                                            )}
                                            {isOpen && isSeo && (
                                                <SeoMegaMenu onClose={() => setOpen(false)} />
                                            )}
                                            {isOpen && isIndustries && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.98 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                                                    role="menu"
                                                    aria-label="القطاعات"
                                                    className="absolute right-1/2 translate-x-1/2 mt-3 w-[min(94vw,640px)] max-h-[80vh] overflow-y-auto bg-white rounded-[24px] z-40 p-8"
                                                    style={{ boxShadow: "0 40px 120px rgba(15,23,42,0.12)" }}
                                                >
                                                    <span className="inline-flex items-center gap-1.5 text-[13px] font-black text-slate-500 mb-5">
                                                        <Building2 className="h-3.5 w-3.5 text-primary-600" aria-hidden="true" />
                                                        استكشف حسب القطاع
                                                    </span>
                                                    <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-right">
                                                        {industriesLinks.map(
                                                            (item) => (
                                                                <a
                                                                    key={item.href}
                                                                    href={item.href}
                                                                    role="menuitem"
                                                                    onClick={() =>
                                                                        setOpen(false)
                                                                    }
                                                                    className="group/item flex items-center justify-between gap-2 rounded-[14px] -mx-3 px-3 py-2.5 text-[14px] font-bold text-slate-700 transition-colors duration-[180ms] hover:bg-[#F8FAFF] hover:text-primary-600"
                                                                >
                                                                    {item.name}
                                                                    <ChevronLeft
                                                                        className="h-3 w-3 text-primary-600 opacity-0 -translate-x-1 transition-all duration-[180ms] group-hover/item:opacity-100 group-hover/item:translate-x-0"
                                                                        aria-hidden="true"
                                                                    />
                                                                </a>
                                                            ),
                                                        )}
                                                    </div>
                                                    <a
                                                        href="/industries"
                                                        onClick={() => setOpen(false)}
                                                        className="mt-6 flex items-center justify-center gap-1.5 rounded-2xl bg-primary-600 px-4 py-3.5 text-[14px] font-black text-white transition-colors hover:bg-primary-700"
                                                    >
                                                        عرض جميع القطاعات
                                                        <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                                                    </a>
                                                </motion.div>
                                            )}
                                            {isOpen && !isServices && !isIndustries && !isSeo && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.98 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                                                    role="menu"
                                                    className="absolute right-0 mt-3 min-w-[240px] bg-white rounded-[24px] z-40 p-2.5"
                                                    style={{ boxShadow: "0 24px 60px rgba(15,23,42,0.10)" }}
                                                >
                                                    {dropdownLinks.map(
                                                        (item) => (
                                                            <a
                                                                key={item.href}
                                                                href={item.href}
                                                                role="menuitem"
                                                                className="block rounded-[12px] px-4 py-3 text-right text-[14px] font-bold text-slate-700 transition-colors duration-150 hover:bg-[#F8FAFF] hover:text-primary-600"
                                                                onClick={() =>
                                                                    setOpen(false)
                                                                }
                                                            >
                                                                {item.name}
                                                            </a>
                                                        ),
                                                    )}
                                                </motion.div>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <a
                                        key={link.name}
                                        href={link.href}
                                        className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-2 font-medium text-[14px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 ${
                                            isActive(link.href)
                                                ? "bg-primary-50 text-primary-600"
                                                : "text-gray-600 hover:text-primary-500"
                                        }`}
                                    >
                                        {link.name}
                                    </a>
                                );
                            })}
                        </nav>

                        <div className="flex shrink-0 items-center gap-4">
                            <a
                                href="/freelancer/register"
                                className="hidden h-12 items-center gap-1.5 rounded-xl border border-primary-200 px-4 text-sm font-semibold text-primary-600 transition-colors duration-150 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 xl:inline-flex"
                            >
                                <UserPlus className="h-4 w-4" aria-hidden="true" />
                                تسجيل كمستقل
                            </a>
                            <a
                                href="/client/register"
                                className="inline-flex h-12 items-center rounded-xl bg-primary-500 px-5 text-sm font-bold text-white transition-colors duration-150 hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
                            >
                                أضف مشروعك
                            </a>
                        </div>
                    </div>

                    {/* Mobile row (below lg): CTA — logo — hamburger (DOM
                        order), which in this `dir="ltr"` row renders as
                        CTA-left / hamburger-right — matching RTL reading
                        order (hamburger at the "start"/right, CTA at the
                        "end"/left). `dir="ltr"` pins both to their visual
                        edges regardless of the page's RTL direction; each
                        element re-declares dir="rtl" so its own content
                        still reads correctly. The logo is absolutely
                        centered on the row (not flex-centered in the
                        leftover space) so it stays true center regardless
                        of the hamburger/CTA's differing widths. */}
                    <div
                        dir="ltr"
                        className={`header-mobile-row relative flex lg:hidden items-center justify-between gap-3 transition-all duration-300 ease-out ${
                            isMobile && hidden ? "px-3 h-[52px]" : "px-4 h-[72px] sm:h-[76px]"
                        }`}
                    >
                        {/* Shrinks below 375px (px-3 + smaller text) — the
                            logo is true-centered on the full row (see the
                            note on the parent header wrapper), so on narrow
                            phones this button's normal size reaches into the
                            centered logo; the standard px-4/text-[13px] size
                            still applies from 375px up, where there's room. */}
                        <a
                            href="/client/register"
                            dir="rtl"
                            className="relative shrink-0 inline-flex items-center gap-2 h-9 pr-4 pl-1 max-[374px]:pr-2.5 rounded-full bg-primary-600 text-white text-[13px] max-[374px]:text-[11px] font-bold whitespace-nowrap shadow-md shadow-primary-600/25 transition-transform duration-200 active:scale-95"
                        >
                            ابدأ الآن
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-primary-600">
                                <ChevronLeft className="h-3.5 w-3.5" />
                            </span>
                        </a>

                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <a href="/" dir="rtl" className="pointer-events-auto">
                                <img
                                    src={wizLogo}
                                    alt="Wiz Freelance"
                                    className="h-9 max-[374px]:h-8 sm:h-10 w-auto object-contain"
                                />
                            </a>
                        </div>

                        <button
                            type="button"
                            onClick={() => setMenuOpen(true)}
                            className={`shrink-0 flex items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition-all duration-300 ease-out hover:bg-slate-200 active:scale-95 ${
                                isMobile && hidden ? "h-9 w-9" : "h-[54px] w-[54px]"
                            }`}
                            aria-expanded={menuOpen}
                            aria-label="فتح قائمة التنقل"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Desktop dropdown backdrop — dims the page behind an open
                mega-menu/dropdown so it reads as a focused overlay instead
                of floating over a fully-interactive page (the mobile
                drawer already had this; desktop didn't). Sits below the
                header bar (z-100) and the dropdown panels (z-40) but above
                page content. Clicking it closes the dropdown, same as the
                existing outside-click handler. */}
            <AnimatePresence>
                {openDesktopDropdown && (
                    <motion.div
                        key="desktop-dropdown-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="hidden lg:block fixed inset-0 z-30 bg-slate-900/30 backdrop-blur-sm"
                        onClick={() => setOpenDesktopDropdown(null)}
                        aria-hidden="true"
                    />
                )}
            </AnimatePresence>

            {/* Mobile drawer */}
            <AnimatePresence>
                {menuOpen && (
                    <React.Fragment key="mobile-drawer">
                        <motion.div
                            key="drawer-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="lg:hidden fixed inset-0 z-[110] bg-slate-900/40 backdrop-blur-sm"
                            onClick={closeDrawer}
                            aria-hidden="true"
                        />
                        <motion.nav
                            key="drawer-panel"
                            dir="rtl"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{
                                duration: 0.3,
                                ease: [0.25, 0.1, 0.25, 1],
                            }}
                            className="lg:hidden fixed inset-y-0 right-0 z-[120] w-full bg-white shadow-2xl flex flex-col"
                            aria-label="قائمة التنقل"
                        >
                            <div className="flex items-center justify-between gap-3 px-5 h-[72px] shrink-0 border-b border-slate-100">
                                <a
                                    href="/"
                                    onClick={closeDrawer}
                                    className="flex items-center"
                                >
                                    <img
                                        src={wizLogo}
                                        alt="Wiz Freelance"
                                        className="h-5 w-auto object-contain"
                                    />
                                </a>
                                <button
                                    type="button"
                                    onClick={closeDrawer}
                                    className="flex items-center justify-center size-11 rounded-xl bg-slate-100 text-slate-700 transition-all duration-200 hover:bg-slate-200 active:scale-95"
                                    aria-label="إغلاق قائمة التنقل"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto px-4 pt-5 pb-4">
                                <div className="flex flex-col gap-1.5 w-full max-w-md mx-auto">
                                    {navLinks.map((link) => {
                                        if (link.dropdown) {
                                            const isOpen =
                                                openDropdown === link.key;
                                            const dropdownLinks =
                                                dropdownGroups[link.key];

                                            return (
                                                <div key={link.name}>
                                                    <button
                                                        className="flex items-center justify-between gap-3 rounded-xl px-4 min-h-12 w-full text-base font-semibold text-slate-700 transition-colors duration-200 hover:bg-primary-50/60"
                                                        onClick={() =>
                                                            setOpenDropdown(
                                                                isOpen
                                                                    ? null
                                                                    : link.key,
                                                            )
                                                        }
                                                        aria-expanded={isOpen}
                                                    >
                                                        {link.name}
                                                        <ChevronDown
                                                            className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                                                                isOpen
                                                                    ? "rotate-180"
                                                                    : ""
                                                            }`}
                                                        />
                                                    </button>
                                                    <AnimatePresence>
                                                        {isOpen && (
                                                            <motion.div
                                                                initial={{
                                                                    height: 0,
                                                                    opacity: 0,
                                                                }}
                                                                animate={{
                                                                    height: "auto",
                                                                    opacity: 1,
                                                                }}
                                                                exit={{
                                                                    height: 0,
                                                                    opacity: 0,
                                                                }}
                                                                transition={{
                                                                    duration: 0.2,
                                                                }}
                                                                className="overflow-hidden"
                                                            >
                                                                <div className="pt-1 pb-2 ps-2">
                                                                    {link.key === "services" ? (
                                                                        // Same 4 category groups as the desktop mega
                                                                        // menu — grouped with mini-headers instead of
                                                                        // one long flat list — plus a "view all" link
                                                                        // to the full catalog.
                                                                        <>
                                                                            {SERVICE_CATEGORIES.map((category) => {
                                                                                const CategoryIcon = MEGA_MENU_ICONS[category.icon];
                                                                                return (
                                                                                    <div key={category.key} className="mb-3 last:mb-0">
                                                                                        <div className="flex items-center gap-1.5 px-4 py-1.5">
                                                                                            {CategoryIcon && (
                                                                                                <CategoryIcon className="h-3.5 w-3.5 text-primary-500" aria-hidden="true" />
                                                                                            )}
                                                                                            <span className="text-[12px] font-black text-slate-400">
                                                                                                {category.title}
                                                                                            </span>
                                                                                        </div>
                                                                                        <ul>
                                                                                            {category.items.map((item) => {
                                                                                                const ItemIcon = MEGA_MENU_ICONS[item.icon];
                                                                                                return (
                                                                                                    <li key={item.href}>
                                                                                                        <a
                                                                                                            href={item.href}
                                                                                                            onClick={closeDrawer}
                                                                                                            className="flex items-center gap-2.5 min-h-11 rounded-lg px-4 text-[15px] font-medium text-slate-600 transition-colors duration-200 hover:bg-primary-50 hover:text-primary-600"
                                                                                                        >
                                                                                                            {ItemIcon && (
                                                                                                                <ItemIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                                                                                                            )}
                                                                                                            {item.name}
                                                                                                        </a>
                                                                                                    </li>
                                                                                                );
                                                                                            })}
                                                                                        </ul>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                            <a
                                                                                href="/services"
                                                                                onClick={closeDrawer}
                                                                                className="mt-2 flex items-center gap-1.5 rounded-lg px-4 min-h-11 text-[14px] font-black text-primary-600"
                                                                            >
                                                                                عرض جميع الخدمات
                                                                                <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                                                                            </a>
                                                                        </>
                                                                    ) : link.key === "seo" ? (
                                                                        // Compact mobile hub (2026-10-10): main SEO page as a
                                                                        // prominent card, then sectors and cities as small
                                                                        // 3-per-row tiles. Full link text kept (anchor text
                                                                        // matters for SEO); data still comes from SeoMegaMenu.
                                                                        <div className="px-3 pb-1">
                                                                            {(() => {
                                                                                const [core, ...groups] = SEO_HUB_CATEGORIES;
                                                                                const [main, ...extras] = core.items;
                                                                                return (
                                                                                    <>
                                                                                        <a
                                                                                            href={main.href}
                                                                                            onClick={closeDrawer}
                                                                                            className="flex items-center gap-3 rounded-xl bg-primary-600 px-3.5 py-3 text-white"
                                                                                        >
                                                                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
                                                                                                <main.icon className="h-5 w-5" aria-hidden="true" />
                                                                                            </span>
                                                                                            <span className="flex-1 text-right">
                                                                                                <span className="block text-[14px] font-black leading-tight">خدمة السيو الشاملة</span>
                                                                                                <span className="block text-[11px] text-white/70 mt-0.5">شركة سيو — تصدّر نتائج Google</span>
                                                                                            </span>
                                                                                            <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
                                                                                        </a>
                                                                                        <div className="mt-2 grid grid-cols-2 gap-2">
                                                                                            {extras.map((item) => (
                                                                                                <a
                                                                                                    key={item.href}
                                                                                                    href={item.href}
                                                                                                    onClick={closeDrawer}
                                                                                                    className="flex items-center justify-center gap-1.5 min-h-11 rounded-xl border border-gray-100 bg-slate-50 px-2 text-[12px] font-bold text-slate-700"
                                                                                                >
                                                                                                    <item.icon className="h-3.5 w-3.5 shrink-0 text-primary-600" aria-hidden="true" />
                                                                                                    {item.name}
                                                                                                </a>
                                                                                            ))}
                                                                                        </div>
                                                                                        {groups.map((group) => (
                                                                                            <div key={group.key} className="mt-3">
                                                                                                <p className="mb-1.5 flex items-center gap-1.5 text-[12px] font-black text-slate-400">
                                                                                                    <group.icon className="h-3.5 w-3.5 text-primary-500" aria-hidden="true" />
                                                                                                    {group.title}
                                                                                                </p>
                                                                                                <div className="grid grid-cols-3 gap-2">
                                                                                                    {group.items.map((item) => (
                                                                                                        <a
                                                                                                            key={item.href}
                                                                                                            href={item.href}
                                                                                                            onClick={closeDrawer}
                                                                                                            className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-blue-100 bg-blue-50/60 px-1.5 py-2 text-center"
                                                                                                            style={{ minHeight: 72 }}
                                                                                                        >
                                                                                                            <item.icon className="h-4 w-4 text-primary-600" aria-hidden="true" />
                                                                                                            <span className="text-[11px] font-bold leading-tight text-slate-700">{item.name}</span>
                                                                                                        </a>
                                                                                                    ))}
                                                                                                </div>
                                                                                            </div>
                                                                                        ))}
                                                                                    </>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    ) : (
                                                                        <ul>
                                                                            {dropdownLinks.map((item) => (
                                                                                <li key={item.href}>
                                                                                    <a
                                                                                        href={item.href}
                                                                                        onClick={closeDrawer}
                                                                                        className="flex items-center min-h-11 rounded-lg px-4 text-[15px] font-medium text-slate-600 transition-colors duration-200 hover:bg-primary-50 hover:text-primary-600"
                                                                                    >
                                                                                        {item.name}
                                                                                    </a>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    )}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            );
                                        }

                                        return (
                                            <a
                                                key={link.name}
                                                href={link.href}
                                                onClick={closeDrawer}
                                                className={`flex items-center rounded-xl px-4 min-h-12 text-base font-semibold transition-colors duration-200 ${
                                                    isActive(link.href)
                                                        ? "bg-primary-50 text-primary-600"
                                                        : "text-slate-700 hover:bg-primary-50/60"
                                                }`}
                                            >
                                                {link.name}
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="shrink-0 px-4 pb-6 pt-3 border-t border-slate-100">
                                <div className="grid grid-cols-2 gap-3 w-full max-w-md mx-auto">
                                    <a
                                        href="/freelancer/register"
                                        onClick={closeDrawer}
                                        className="flex items-center justify-center gap-1.5 h-12 rounded-xl border border-primary-200 text-primary-600 font-bold transition-colors duration-200 active:scale-95"
                                    >
                                        <UserPlus className="h-4 w-4" aria-hidden="true" />
                                        تسجيل كمستقل
                                    </a>
                                    <a
                                        href="/client/register"
                                        onClick={closeDrawer}
                                        className="flex items-center justify-center h-12 rounded-xl bg-gradient-to-l from-primary-500 to-primary-600 text-white font-bold shadow-sm transition-transform duration-200 active:scale-95"
                                    >
                                        أضف مشروعك
                                    </a>
                                </div>
                            </div>
                        </motion.nav>
                    </React.Fragment>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Header;
